import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clapperboard, Film, Mic2, RefreshCw, ShieldCheck, Sparkles, Video } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  title: '',
  asset_type: 'video',
  source_url: '',
  source_origin: 'upload',
  duration_minutes: '15',
  primary_language: 'es',
  protagonist_name: '',
  brand_context: '',
  target_audience: '',
  objective: '',
  rights_status: 'pending_review',
  allow_voice_clone: true,
  allow_face_clone: true,
  notes: '',
  tags: 'video, autoridad, orgánico',
};

export default function MediaLabHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const loadHub = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const res = await authFetch(`${API}/admin/media-lab`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar Media Lab');
      setHub(data);
      if (showToast) toast.success('Media Lab actualizado');
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar Media Lab');
    } finally {
      setRefreshing(false);
    }
  }, [authFetch]);

  useEffect(() => {
    loadHub(false);
  }, [loadHub]);

  const createAsset = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await authFetch(`${API}/admin/media-lab/assets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          duration_minutes: Number(form.duration_minutes),
          tags: form.tags.split(',').map((item) => item.trim()).filter(Boolean),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo guardar el asset');
      toast.success('Asset guardado en Media Lab');
      setForm(initialForm);
      loadHub(false);
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar el asset');
    } finally {
      setSaving(false);
    }
  };

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="media-lab-loading">Cargando Media Lab...</div>;

  return (
    <div className="space-y-6" data-testid="media-lab-hub">
      <div className="glass-card tracing-panel p-6" data-testid="media-lab-hero-card">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Media Lab</p>
        <h3 className="mt-2 text-3xl font-semibold text-text-heading">Sistema interno de vídeos, clips, clonación y campañas orgánicas</h3>
        <p className="mt-3 text-sm text-text-body">Aquí se convierte cualquier vídeo en una máquina de contenido: autoridad, hooks, clips, YouTube, plantillas y readiness para voz/cara con control legal.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {[
          ['Assets totales', hub.metrics.total_assets, 'Base de trabajo viva', Video, 'media-lab-metric-total'],
          ['Vídeos', hub.metrics.video_assets, 'Entrevistas, reels y largos', Film, 'media-lab-metric-videos'],
          ['Listos para clonación', hub.metrics.clone_ready_assets, 'Con base facial/voz', Mic2, 'media-lab-metric-clone'],
          ['Listos para orgánico', hub.metrics.organic_ready_assets, 'Distribución multicanal', Sparkles, 'media-lab-metric-organic'],
          ['Campañas activas', hub.metrics.active_campaigns, 'Series y pipelines', Clapperboard, 'media-lab-metric-campaigns'],
        ].map(([label, value, helper, Icon, testId]) => (
          <div key={testId} className="glass-card p-5" data-testid={testId}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
                <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
                <p className="mt-2 text-xs text-text-muted">{helper}</p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <form onSubmit={createAsset} className="glass-card p-6 space-y-4" data-testid="media-lab-create-form">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Nuevo asset</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Alta rápida de vídeo o activo reutilizable</h4>
            </div>
            <button type="button" onClick={() => loadHub(true)} className="btn-secondary" data-testid="media-lab-refresh-button">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refrescar
            </button>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <input className="input-kinesthetic" placeholder="Título" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} data-testid="media-lab-title-input" />
            <input className="input-kinesthetic" placeholder="URL del asset" value={form.source_url} onChange={(e) => setForm((prev) => ({ ...prev, source_url: e.target.value }))} data-testid="media-lab-url-input" />
            <input className="input-kinesthetic" placeholder="Protagonista" value={form.protagonist_name} onChange={(e) => setForm((prev) => ({ ...prev, protagonist_name: e.target.value }))} data-testid="media-lab-protagonist-input" />
            <input className="input-kinesthetic" placeholder="Contexto de marca" value={form.brand_context} onChange={(e) => setForm((prev) => ({ ...prev, brand_context: e.target.value }))} data-testid="media-lab-brand-input" />
            <input className="input-kinesthetic" placeholder="Audiencia" value={form.target_audience} onChange={(e) => setForm((prev) => ({ ...prev, target_audience: e.target.value }))} data-testid="media-lab-audience-input" />
            <input className="input-kinesthetic" placeholder="Duración (min)" value={form.duration_minutes} onChange={(e) => setForm((prev) => ({ ...prev, duration_minutes: e.target.value }))} data-testid="media-lab-duration-input" />
            <textarea className="input-kinesthetic resize-none md:col-span-2" rows={3} placeholder="Objetivo del asset" value={form.objective} onChange={(e) => setForm((prev) => ({ ...prev, objective: e.target.value }))} data-testid="media-lab-objective-input" />
            <textarea className="input-kinesthetic resize-none md:col-span-2" rows={3} placeholder="Notas" value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} data-testid="media-lab-notes-input" />
            <input className="input-kinesthetic md:col-span-2" placeholder="Tags separadas por coma" value={form.tags} onChange={(e) => setForm((prev) => ({ ...prev, tags: e.target.value }))} data-testid="media-lab-tags-input" />
          </div>
          <div className="flex flex-wrap gap-3 text-sm text-text-body">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.allow_voice_clone} onChange={(e) => setForm((prev) => ({ ...prev, allow_voice_clone: e.target.checked }))} data-testid="media-lab-voice-clone-checkbox" /> Voz clonable</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.allow_face_clone} onChange={(e) => setForm((prev) => ({ ...prev, allow_face_clone: e.target.checked }))} data-testid="media-lab-face-clone-checkbox" /> Cara clonable</label>
          </div>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="media-lab-submit-button">{saving ? 'Guardando...' : 'Guardar asset'}</button>
        </form>

        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="media-lab-organic-board-card">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Lanzamiento orgánico</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Qué falta para empezar a vender en orgánico</h4>
            <p className="mt-3 text-sm text-text-body">{hub.organic_launch_board.goal}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="media-lab-organic-channels">
              {(hub.organic_launch_board.channels || []).map((item) => <span key={item} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}
            </div>
            <div className="mt-5 grid gap-3">
              {(hub.organic_launch_board.minimum_assets_needed || []).map((item, index) => (
                <div key={index} className="rounded-[18px] border border-border-subtle bg-background/55 p-4 flex items-start gap-3" data-testid={`media-lab-minimum-asset-${index}`}>
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-primary" />
                  <p className="text-sm text-text-body">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="media-lab-ceo-alerts-card">
            <div className="flex items-center gap-2 text-text-heading">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h4 className="text-lg font-semibold">Alertas ejecutivas preparadas</h4>
            </div>
            <p className="mt-3 text-sm text-text-body">WhatsApp CEO objetivo: {hub.ceo_alert_settings.whatsapp_number}. Canales activos previstos: {(hub.ceo_alert_settings.enabled_channels || []).join(', ')}.</p>
            <p className="mt-2 text-xs text-text-muted">Falta conectar credenciales reales de WhatsApp Cloud API para que nuevos clientes, pagos, tareas críticas y órdenes CEO lleguen a tu WhatsApp personal automáticamente.</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {(hub.assets || []).map((asset) => (
          <div key={asset.id} className="glass-card p-6" data-testid={`media-lab-asset-${asset.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{asset.asset_type}</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">{asset.title}</h4>
                <p className="mt-2 text-sm text-text-body">{asset.objective}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-border-subtle px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{asset.rights_status}</span>
                <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{asset.clone_readiness?.recommended_use || 'content_only'}</span>
              </div>
            </div>

            <div className="mt-5 grid gap-4 xl:grid-cols-3">
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`media-lab-asset-hooks-${asset.id}`}>
                <p className="text-sm font-semibold text-text-heading">Hooks base</p>
                <div className="mt-3 space-y-2">
                  {(asset.repurposing_blueprint?.hooks || []).map((item, index) => <p key={index} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`media-lab-asset-angles-${asset.id}`}>
                <p className="text-sm font-semibold text-text-heading">Ángulos de contenido</p>
                <div className="mt-3 space-y-2">
                  {(asset.repurposing_blueprint?.angles || []).map((item, index) => <p key={index} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
              <div className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`media-lab-asset-youtube-${asset.id}`}>
                <p className="text-sm font-semibold text-text-heading">YouTube y formatos largos</p>
                <div className="mt-3 space-y-2">
                  {(asset.repurposing_blueprint?.youtube_formats || []).map((item, index) => <p key={index} className="text-xs text-text-muted">• {item}</p>)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}